import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    MegaphoneIcon,
    CalendarDaysIcon,
    PlusCircleIcon,
    TrashIcon,
    PencilSquareIcon,
    ClockIcon,
    UserIcon,
} from '@heroicons/react/24/outline';

const initialAnnouncements = [
    {
        id: 1,
        title: 'Company-wide Quarterly Meeting',
        content: 'Join us for our Q3 company meeting to discuss our performance and goals for the next quarter. All employees are required to attend.',
        author: 'HR Department',
        date: 'July 25, 2024',
        type: 'Announcement',
    },
    {
        id: 2,
        title: 'New Policy on Remote Work',
        content: 'We are updating our remote work policy to offer more flexibility. Please review the new guidelines in the Documents section.',
        author: 'Management',
        date: 'July 22, 2024',
        type: 'Policy',
    },
    {
        id: 3,
        title: 'July Employee of the Month',
        content: 'Congratulations to Jane Doe from the Technology department for her outstanding contributions this month!',
        author: 'HR Department',
        date: 'July 20, 2024',
        type: 'Recognition',
    },
];

const InternalCommunications = () => {
    const [announcements, setAnnouncements] = useState(initialAnnouncements);
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [selectedType, setSelectedType] = useState('Announcement');

    const handlePostAnnouncement = (e) => {
        e.preventDefault();
        const newAnnouncement = {
            id: announcements.length + 1,
            title,
            content,
            author: 'Admin User', // In a real app, this would be the logged-in user
            date: new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
            type: selectedType,
        };
        setAnnouncements([newAnnouncement, ...announcements]);
        setTitle('');
        setContent('');
        setSelectedType('Announcement');
    };

    const handleDelete = (id) => {
        setAnnouncements(announcements.filter(announcement => announcement.id !== id));
    };

    const getTypeColor = (type) => {
        switch (type) {
            case 'Announcement':
                return 'bg-blue-100 text-blue-800';
            case 'Policy':
                return 'bg-purple-100 text-purple-800';
            case 'Recognition':
                return 'bg-green-100 text-green-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-gray-100 to-slate-100 p-8">
            <motion.div
                initial={{ opacity: 0, y: -30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="max-w-7xl mx-auto"
            >
                {/* Header */}
                <div className="flex flex-col md:flex-row items-center justify-between mb-8 pb-4 border-b-2 border-slate-400">
                    <h1 className="text-4xl font-extrabold text-slate-800 tracking-tight mb-4 md:mb-0">
                        Internal Communications
                    </h1>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Announcement Feed */}
                    <motion.div
                        className="lg:col-span-2 bg-white rounded-3xl shadow-xl p-6"
                        initial={{ opacity: 0, x: -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2, duration: 0.6 }}
                    >
                        <div className="flex items-center space-x-2 mb-6 text-slate-600">
                            <MegaphoneIcon className="h-7 w-7" />
                            <h2 className="text-2xl font-bold">Latest Announcements</h2>
                        </div>
                        <AnimatePresence>
                            {announcements.length > 0 ? (
                                announcements.map(announcement => (
                                    <motion.div
                                        key={announcement.id}
                                        className="bg-gray-50 rounded-xl shadow-sm p-5 mb-4 border border-gray-200"
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, x: -20 }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        <div className="flex items-center justify-between mb-2">
                                            <h3 className="text-lg font-bold text-gray-800">{announcement.title}</h3>
                                            <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getTypeColor(announcement.type)}`}>
                                                {announcement.type}
                                            </span>
                                        </div>
                                        <p className="text-gray-600 mb-3">{announcement.content}</p>
                                        <div className="flex items-center justify-between text-sm text-gray-500">
                                            <div className="flex items-center space-x-2">
                                                <UserIcon className="h-4 w-4" />
                                                <span>{announcement.author}</span>
                                                <ClockIcon className="h-4 w-4 ml-4" />
                                                <span>{announcement.date}</span>
                                            </div>
                                            <div className="flex space-x-2">
                                                <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} className="text-purple-600 hover:text-purple-800">
                                                    <PencilSquareIcon className="h-5 w-5" />
                                                </motion.button>
                                                <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} className="text-red-600 hover:text-red-800" onClick={() => handleDelete(announcement.id)}>
                                                    <TrashIcon className="h-5 w-5" />
                                                </motion.button>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))
                            ) : (
                                <motion.div
                                    key="no-announcements"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    className="p-10 text-center text-gray-400 rounded-xl border-dashed border-2 border-gray-300"
                                >
                                    <MegaphoneIcon className="h-16 w-16 mx-auto mb-4 text-gray-400" />
                                    <p className="text-lg font-medium">No announcements yet.</p>
                                    <p className="text-sm mt-1">Start by posting a new announcement on the right.</p>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.div>

                    {/* Post Announcement Form & Calendar */}
                    <motion.div
                        className="lg:col-span-1 space-y-8"
                        initial={{ opacity: 0, x: 30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3, duration: 0.6 }}
                    >
                        {/* Post Announcement Form */}
                        <div className="bg-white rounded-3xl shadow-xl p-6">
                            <div className="flex items-center space-x-2 mb-6 text-slate-600">
                                <PlusCircleIcon className="h-7 w-7" />
                                <h2 className="text-2xl font-bold">Post New</h2>
                            </div>
                            <form onSubmit={handlePostAnnouncement} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Title</label>
                                    <input
                                        type="text"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 bg-gray-50 p-3"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Type</label>
                                    <select
                                        value={selectedType}
                                        onChange={(e) => setSelectedType(e.target.value)}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 bg-gray-50 p-3"
                                    >
                                        <option>Announcement</option>
                                        <option>Policy</option>
                                        <option>Recognition</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Content</label>
                                    <textarea
                                        value={content}
                                        onChange={(e) => setContent(e.target.value)}
                                        rows="4"
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 bg-gray-50 p-3"
                                        required
                                    ></textarea>
                                </div>
                                <motion.button
                                    type="submit"
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    className="w-full flex justify-center items-center space-x-2 px-6 py-3 bg-indigo-600 text-white rounded-full shadow-lg hover:bg-indigo-700 transition-colors"
                                >
                                    <MegaphoneIcon className="h-5 w-5" />
                                    <span className="font-semibold">Post Announcement</span>
                                </motion.button>
                            </form>
                        </div>

                        {/* Calendar */}
                        <div className="bg-white rounded-3xl shadow-xl p-6">
                            <div className="flex items-center space-x-2 mb-4 text-slate-600">
                                <CalendarDaysIcon className="h-7 w-7" />
                                <h2 className="text-2xl font-bold">Company Events</h2>
                            </div>
                            <div className="text-center text-gray-500 p-4 border-dashed border-2 border-gray-300 rounded-xl">
                                <p>Calendar functionality to be added here.</p>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </motion.div>
        </div>
    );
};

export default InternalCommunications;